/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Corporate Header menu item component
 *
 * @author Yakov Gushchin
 * @version 1.16.0
 * @since 1.12.0
 */

import React, { FC, useCallback, useState } from 'react';

import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { icons } from '../../../assets';
import { pushGTMAnalytic } from '../../../modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '../../../modules/analytics/GoogleTagManager/GTMOperations';
import { KeyboardKeyCodeEnum } from '../../../modules/app/appConstants';
import { ILogBusinessEventPayload } from '../../../modules/logging/loggingTypes';
import { ONBOARDING_DATA_ATTR } from '../../../modules/onboarding/onboardingConstants';
import { openRoute } from '../../../routes/constants/externalRoutes';
import { checkInternalUrlIsValid } from '../../../utils/validatorUtils';
import { EllipseText } from '../../common';
import { ImgStyled } from '../../common/Block/Img';
import { LinkTextBlock } from '../../common/LinkBlock/styled/LinkStyled';
import { ICorporateHeaderConfigItem, ICorporateHeaderConfigSubItem } from '../corporateHeaderTypes';
import { CorporateHeaderMenuItemStyled } from './styled/CorporateHeaderMenuItemStyled';

interface ICorporateHeaderMenuItem extends Exclude<ICorporateHeaderConfigItem, 'url' | 'urlIsExternal'> {
    logBusinessEvent: (payload: ILogBusinessEventPayload) => void;
    selected: boolean;
    url: string;
    urlIsExternal?: boolean;
}

const CorporateHeaderMenuItem: FC<ICorporateHeaderMenuItem> = props => {

    const {
        children,
        dataAttr,
        logBusinessEvent,
        onboarding,
        selected,
        subItems,
        ...item
    } = props;

    const [isHover, setHover] = useState(false);

    const dispatch = useDispatch();
    const onSelectItemHandler = useCallback((eventName, eventType, title) => {
        setHover(false);

        dispatch(
            pushGTMAnalytic({
                operation: GTMOperations.navigation.tabTopClickOperation(title, 'TopMenu'),
                withAccountType: true,
                withSusrId: true,
                withUserRole: true,
            }));

        if (eventName && eventType && logBusinessEvent) {
            logBusinessEvent({ eventName: eventName, type: eventType });
        }
    }, [dispatch, logBusinessEvent]);

    const onSelectRootItemHandler = useCallback(() => {
        if (subItems?.length) return;

        onSelectItemHandler(item.eventName, item.eventType, item.title);
    }, [item, subItems, onSelectItemHandler]);

    const onSelectSubItemHandler = useCallback((subItem: ICorporateHeaderConfigSubItem): void => {
        onSelectItemHandler(subItem.eventName, subItem.eventType, subItem.title);
    }, [onSelectItemHandler]);

    const onKeyDownHandler = useCallback((event: React.KeyboardEvent): void => {
        if (event.keyCode === KeyboardKeyCodeEnum.ENTER && !subItems?.length) {
            openRoute(item.url);
            onSelectItemHandler(item.eventName, item.eventType, item.title);
        }

        if (event.keyCode === KeyboardKeyCodeEnum.ESCAPE) {
            setHover(false);
        }

        if (event.keyCode === KeyboardKeyCodeEnum.ENTER) {
            setHover(!isHover && !!subItems?.length);
        }

    }, [item, isHover, subItems, onSelectItemHandler]);

    const onMouseDownHandler = useCallback((event: React.MouseEvent, callback: () => void) => {
        if (event.button === 1) {
            event.preventDefault();
            callback();
        }
    }, []);

    return (
        <CorporateHeaderMenuItemStyled
            className={classNames('tab-menu-item-container', { isHover, selected })}
            data-menu-item={dataAttr}
            onClick={onSelectRootItemHandler}
            onKeyDown={onKeyDownHandler}
            onMouseDown={e => onMouseDownHandler(e, onSelectRootItemHandler)}
            {...{ [ONBOARDING_DATA_ATTR]: onboarding }}
        >
            <div className="tab-menu__link" >
                <EllipseText showTooltipOnOverflow={false}>
                    {
                        item.url
                            ? item.urlIsExternal
                                // External URL
                                ? checkInternalUrlIsValid(item.url) && <a href={item.url} tabIndex={-1}>{item.title}</a>
                                // React URL
                                : <Link tabIndex={-1} to={item.url}>{item.title}</Link>
                            // For links with sub items show only text
                            : item.title
                    }
                </EllipseText>
                {
                    subItems
                        ? <ImgStyled className="small tab-menu__link-arrow" src={icons.arrowListDownSmall}/>
                        : children
                }
            </div>

            {
                subItems && (
                    <div className="tab-menu__drop-down">
                        {subItems.map(subItem => (
                            <Link
                                className="drop-down__link"
                                key={subItem.url}
                                to={subItem.url}
                                onClick={() => onSelectSubItemHandler(subItem)}
                                onMouseDown={e => onMouseDownHandler(e, () => onSelectSubItemHandler(subItem))}
                            >
                                <LinkTextBlock>{subItem.title}</LinkTextBlock>
                            </Link>
                        ))}
                    </div>
                )
            }
        </CorporateHeaderMenuItemStyled>
    );
};

export default CorporateHeaderMenuItem;
